// src/pages/marketing/HomePage.jsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth-context";
import Navbar from "../../components/marketing/Navbar";
import Hero from "../../components/marketing/Hero";
import ContentBlock from "../../components/marketing/ContentBlock";
import CTAContainer from "../../components/marketing/CTAContainer";
import Footer from "../../components/marketing/Footer";

const HomePage = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return (
    <div className="min-h-screen text-charcoal-800 font-['Lexend_Deca'] text-base leading-relaxed">
      <Navbar />

      <Hero
        title="Haal meer uit verandertaal"
        subtitle="Online trainen in motiverende gespreksvoering voor paramedici"
        ctaText="Bekijk de training"
        ctaLink="/haalmeeruitverandertaal"
        height="full"
      />

      <main className="mx-auto max-w-3xl py-20 px-5">
        <ContentBlock title="Oefen gespreksvaardigheden wanneer het jou uitkomt">
          <p>
            Train motiverende gespreksvoering in realistische gesprekssimulaties
            en ontvang direct persoonlijke feedback. Onze eerste training is
            geaccrediteerd voor <strong>5,5 punten bij ADAP</strong>.
          </p>
        </ContentBlock>

        <ContentBlock title="Herken je dit?">
          <p>
            Je weet dat je méér uit de eigen motivatie van je cliënt kunt halen,
            maar in gesprekken lukt het nog niet om de theorie in de praktijk te
            brengen. Je zoekt een manier om vaker te oefenen, zonder afhankelijk
            te zijn van trainingsdagen die tijdrovend en prijzig zijn.
          </p>
        </ContentBlock>

        <ContentBlock
          title="Meer dan een e-learning, zonder de drempel van klassikale training"
          highlight
        >
          <p>
            Stel je voor: je oefent gesprekstechnieken gewoon achter je laptop,
            in je eigen tempo. Geen reistijd, geen volle trainingsdagen, maar
            wel gericht oefenen op een moment dat het jou uitkomt.
          </p>
        </ContentBlock>

        <ContentBlock title="De e-learning die wél interactief is">
          <p>
            Veel e-learnings blijven hangen in video's kijken en meerkeuzevragen
            maken. Onze training is anders. Je oefent gespreksvaardigheden in
            een gesimuleerd gesprek met een{" "}
            <strong>virtuele gesprekspartner</strong>. Je krijgt korte stukjes
            theorie, past die direct toe en ontvangt gerichte feedback op jouw
            gesprekstechnieken.
          </p>
        </ContentBlock>

        <ContentBlock title="Wat je kunt verwachten">
          <ul className="list-disc pl-5 space-y-2">
            <li>Korte, gerichte theorielessen per techniek</li>
            <li>Realistische gesprekssimulaties waarin je direct oefent</li>
            <li>Persoonlijke feedback op jouw gespreksvaardigheden</li>
            <li>Oefenen in je eigen tempo, waar en wanneer het jou uitkomt</li>
          </ul>
        </ContentBlock>

        <ContentBlock title="Waarom deze aanpak werkt" highlight>
          <p>
            Onze trainingen zijn ontwikkeld voor zorgprofessionals die hun
            cliënten effectiever willen begeleiden bij gedragsverandering. Door
            de combinatie van hapklare theorie, directe toepassing, herhaling en
            feedback worden vaardigheden niet alleen begrepen, maar ook
            daadwerkelijk geoefend.
          </p>
        </ContentBlock>

        <ContentBlock title="Voor wie is dit?">
          <p>
            Deze training is bedoeld voor paramedici en andere zorgprofessionals
            die actief willen oefenen met motiverende gespreksvoering. Niet voor
            wie alleen kennis wil opdoen, maar voor wie echt vaardiger wil
            worden in gesprekken met cliënten.
          </p>
        </ContentBlock>

        <ContentBlock title="Hoe werkt de online simulatietraining?">
          <p>
            Je oefent per onderdeel één specifieke techniek. Eerst krijg je een
            korte uitleg, daarna ga je in gesprek met een{" "}
            <strong>virtuele gesprekspartner</strong>. Vervolgens ontvang je
            feedback op jouw gespreksvaardigheden, zodat je de theorie direct
            leert toepassen in een realistische situatie.
          </p>
        </ContentBlock>

        <ContentBlock title="Over LerenMotiveren">
          <p>
            LerenMotiveren ontwikkelt interactieve online trainingen voor
            zorgprofessionals die hun cliënten beter willen begeleiden bij
            gedragsverandering. Met behulp van AI-technologie maken we oefenen
            toegankelijker, praktischer en effectiever dan in traditionele
            e-learnings.
          </p>
        </ContentBlock>

        <CTAContainer
          title="Spreekt dit je aan?"
          text="Bekijk de e-learning 'Haal meer uit verandertaal' en ontdek hoe je online kunt oefenen met directe feedback."
          buttonText="Meer over de training"
          buttonLink="/haalmeeruitverandertaal"
          secondaryButtonText="Inschrijven"
          secondaryButtonLink="/register"
        />
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
